import * as React from 'react';
import {
  FormProvider,
  useFormContext,
  Controller,
  type UseFormReturn,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
// zod resolver intentionally not used here to keep the helper lean
import { cn } from '../../lib/utils';
import { Label } from './label';

// Form Context
interface FormContextValue {
  autoSave?: boolean;
  onAutoSave?: (data: unknown) => void;
}

const FormContext = React.createContext<FormContextValue>({});

// Form Provider Component
interface FormProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'autoSave' | 'onSubmit'
> {
  form: UseFormReturn<TFieldValues>;
  onSubmit: (data: TFieldValues) => void | Promise<void>;
  autoSave?: boolean;
  autoSaveDelay?: number;
  onAutoSave?: (data: TFieldValues) => void | Promise<void>;
  children: React.ReactNode;
}

function Form<TFieldValues extends FieldValues = FieldValues>({
  form,
  onSubmit,
  autoSave = false,
  autoSaveDelay = 1000,
  onAutoSave,
  children,
  className,
  ...props
}: FormProps<TFieldValues>) {
  const autoSaveTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-save functionality
  React.useEffect(() => {
    if (!autoSave || !onAutoSave) return;

    const subscription = form.watch((data) => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      autoSaveTimeoutRef.current = setTimeout(() => {
        if (form.formState.isDirty && form.formState.isValid) {
          onAutoSave(data as TFieldValues);
        }
      }, autoSaveDelay);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = null;
    };
  }, [form, autoSave, onAutoSave, autoSaveDelay]);

  const contextValue: FormContextValue = {
    autoSave,
    onAutoSave: onAutoSave
      ? (data: unknown) => {
          void onAutoSave(data as TFieldValues);
        }
      : undefined,
  };

  return (
    <FormContext.Provider value={contextValue}>
      <FormProvider {...form}>
        <form
          className={cn('space-y-6', className)}
          onSubmit={form.handleSubmit(onSubmit)}
          {...props}
        >
          {children}
        </form>
      </FormProvider>
    </FormContext.Provider>
  );
}

// Form Field Component
interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  label?: string;
  description?: string;
  required?: boolean;
  children: (field: {
    value: unknown;
    onChange: (value: unknown) => void;
    onBlur: () => void;
    error?: string;
    invalid: boolean;
  }) => React.ReactNode;
}

function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, label, description, required = false, children }: FormFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className="space-y-2">
          {label && (
            <Label htmlFor={field.name} className="flex items-center gap-1">
              {label}
              {required && <span className="text-error-500">*</span>}
            </Label>
          )}
          {children({
            value: field.value,
            onChange: field.onChange,
            onBlur: field.onBlur,
            error: fieldState.error?.message,
            invalid: !!fieldState.error,
          })}
          {description && !fieldState.error && (
            <p className="text-xs text-text-secondary">{description}</p>
          )}
        </div>
      )}
    />
  );
}

// Form Message Component
interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  error?: boolean;
}

const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, error = false, children, ...props }, ref) => {
    if (!children) return null;

    return (
      <p
        ref={ref}
        className={cn('text-xs', error ? 'text-error-500' : 'text-text-secondary', className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);
FormMessage.displayName = 'FormMessage';

// Form Item Component for layout
const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('space-y-2', className)} {...props} />;
  }
);
FormItem.displayName = 'FormItem';

// Form Label Component
interface FormLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  required?: boolean;
}

const FormLabel = React.forwardRef<HTMLLabelElement, FormLabelProps>(
  ({ className, required = false, children, ...props }, ref) => {
    return (
      <Label ref={ref} className={cn('flex items-center gap-1', className)} {...props}>
        {children}
        {required && <span className="text-error-500">*</span>}
      </Label>
    );
  }
);
FormLabel.displayName = 'FormLabel';

// Hook to access form context
function useFormStatus() {
  const form = useFormContext();
  const context = React.useContext(FormContext);

  return {
    isDirty: form.formState.isDirty,
    isValid: form.formState.isValid,
    isSubmitting: form.formState.isSubmitting,
    errors: form.formState.errors,
    autoSave: context.autoSave,
  };
}

export { Form, FormField, FormItem, FormLabel, FormMessage, useFormStatus, useFormContext };
